import axios from "axios";
import Sentry from "./Sentry";
import { resolveLanguage } from "./i18n/locale";
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Storefront i18n: 4 locales supported by the backend catalogue localization
// (kayana-web-service), keyed off the optional `language` request header.
// Anything else (including English) omits the header, which the backend
// treats as its default/English behaviour — so omission is byte-identical
// to today.
//
// resolveLanguage lives in ./i18n/locale.js and is shared with the UI
// language (i18next) so the two can never drift.
export const resolveLanguageHeader = resolveLanguage;

// No response at all = the user lost connectivity, navigated away or timed out.
// Not actionable, so not reported to Sentry. Any HTTP status (4xx/5xx) is still reported.
export const isTransientNetworkError = (error) =>
  !error?.response &&
  ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error?.code);

// Only non-personal fields go to Sentry: request and response bodies can hold
// customer names, phones, emails and addresses. requestId links to backend logs.
export const apiErrorContext = (error) => ({
  url: error?.config?.url?.split("?")[0],
  method: error?.config?.method,
  status: error?.response?.status,
  requestId: error?.response?.data?.requestId,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const language = resolveLanguageHeader();
    
    // Omit language header only when viewing a receipt with an explicit language code in the URL
    // e.g. /receipt/Order_123/pt (which means we want base-language variables from the API, and translated UI)
    const isLocalizedReceiptPage = window.location.pathname.match(/^\/receipt\/[^\/]+\/[a-zA-Z]{2,3}\/?$/);
    
    if (language && !isLocalizedReceiptPage) {
      config.headers = config.headers || {};
      config.headers["language"] = language;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!isTransientNetworkError(error)) {
      // Per-event context, not setContext: setContext would stick to every later event.
      Sentry.captureException(error, {
        contexts: { "API Error": apiErrorContext(error) },
      });
    }

    return Promise.reject(
      (error.response && error.response.data) || "Something went wrong!"
    );
  }
);

export default axiosInstance;
